import React, { useState, useCallback } from 'react';
import styled from 'styled-components';

import { Tooltip } from '@shared/Tooltip';
import { useViewport } from '@shared/hooks/useViewport';
import { BottomSheets } from '@shared/BottomSheets';
import { Badge } from '../common/Badge';
import media from '../../utils/mediaQueries';
import Btn from '../buttons/Btn';
import Facebook from '../../images/icons-facebook.inline.svg';
import { NOTES_COLOR } from '../../theme';
import { FacebookTooltip } from './FacebookTooltip';
import { AnnonceCategory } from '../../enums';
import { Modal } from './Modal';
import { RESOLUTION } from '../../constants';

const AnnonceBadge = styled(Badge)`
  padding: 24px 32px;
  margin-bottom: 16px;
`;

const Container = styled.div`
  ${media.mobile`
    position: fixed;
    bottom: 0;
    left: 0;
    right: 0;
    z-index: 2; 
    padding: 16px 24px;
    background: #fff;
    border-top-left-radius: 12px;
    border-top-right-radius: 12px;
    box-shadow: 0 10px 25px 0 rgba(0, 0, 0, 0.4);
    
    ${AnnonceBadge} {
      padding: 0;
    }
  `}
`;

const Title = styled.h3`
  font-size: 24px;
  font-weight: 800;
  line-height: 1.25;
  letter-spacing: 0.2px;
  margin: 0 0 24px;

  ${media.mobile`
    font-size: 18px;
  `}
`;

const Share = styled.div`
  display: flex;
  align-items: center;
  cursor: pointer;
  justify-content: center;
  margin-bottom: 40px;

  ${media.mobile`
    justify-content: flex-start;
    margin-bottom: 0;
  `}
`;

const ShareText = styled.span`
  font-size: 14px;
  line-height: 1.29;
  letter-spacing: -0.1px;
  color: ${NOTES_COLOR};
  margin-left: 8px;
`;

const FacebookIcon = styled(Facebook)`
  width: 24px;
  height: 24px;

  ${media.mobile`
    width: 16px;
    height: 16px;
  `}
`;

type AnnonceAsideProps = {
  annonceId: string;
  category: AnnonceCategory;
  title: string;
};

const AnnonceAside = ({ annonceId, category, title }: AnnonceAsideProps) => {
  const [isModalOpened, setModalOpened] = useState(false);
  const { viewportWidth } = useViewport();

  const handleResponseClick = useCallback(() => {
    setModalOpened(true);
  }, []);

  const handleModalCloseClick = useCallback(() => {
    setModalOpened(false);
  }, []);

  return (
    <Container>
      <AnnonceBadge>
        <Title>Cette annonce vous intéresse&nbsp;?</Title>
        <Btn text="Répondre à cette annonce" block onClick={handleResponseClick} />
      </AnnonceBadge>
      {isModalOpened && <Modal title={title} onClose={handleModalCloseClick} annonceId={annonceId} />}
      {
        viewportWidth <= RESOLUTION.LARGE
          ? (
            <BottomSheets button={(
              <Share>
                <FacebookIcon />
                <ShareText>Partager cette annonce sur Facebook</ShareText>
              </Share>
            )}
            >
              <FacebookTooltip annonceId={annonceId} category={category} />
            </BottomSheets>
          )
          : (
            <Tooltip
              content={<FacebookTooltip annonceId={annonceId} category={category} />}
              position="bottom right"
              positionMobile="top left"
              trigger="click"
              width="auto"
            >
              <Share>
                <FacebookIcon />
                <ShareText>Partager cette annonce sur Facebook</ShareText>
              </Share>
            </Tooltip>
          )
      }
    </Container>
  );
};

export { AnnonceAside };
